import { microServicesRoutes } from '@constants';
import { edgeApi } from '@lib/apiClients.ts';
import { logError } from '@lib/logger/logError.ts';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';
import  type { AxiosResponse } from 'axios';
import type {
  ActiveOrganizationsResponse,
  CreateOrganizationDto,
  OrganizationManagement,
  UpdateOrganizationDto,
} from '@types';
import { useCallback, useMemo } from 'react';

export interface ApiError { message: string; raw?: string }
export type ApiResponse<T> =
  | { success: true; data: T; totalPages?: number; totalCount?: number; pageNumber?: number }
  | { success: false; error: ApiError };

export default function useOrganizationsEdgeApi (): {
  getOrganizationsAsync: (
    pageNumber: number,
    pageLimit: number,
    search: string
  ) => Promise<ApiResponse<OrganizationManagement[]>>;
  getActiveOrganizationsAsync: () => Promise<ApiResponse<ActiveOrganizationsResponse[]>>;
  createOrganizationAsync: (org: CreateOrganizationDto) => Promise<ApiResponse<OrganizationManagement>>;
  updateOrganizationAsync: (org: UpdateOrganizationDto) => Promise<ApiResponse<OrganizationManagement>>;
  deleteOrganizationAsync: (id: string) => Promise<ApiResponse<OrganizationManagement>>;
  } {
  const handleError = useCallback(<T = never>(
    message: string,
    error: unknown,
    source: string
  ): ApiResponse<T> => {
    const errorMessage = extractErrorMessage(error);
    void logError(message, source, { error: errorMessage, rawError: error }, true);
    return { success: false, error: { message, raw: errorMessage } };
  }, []);

  const getOrganizationsAsync = useCallback(
    async (
      pageNumber: number,
      pageLimit: number,
      search: string
    ): Promise<ApiResponse<OrganizationManagement[]>> => {
      const safeSearch = search === 'undefined' ? '' : search;

    interface ServerDto {
      data: OrganizationManagement[];
      totalPages: number;
      totalCount: number;
      pageNumber: number;
    }

    try {
      const res: AxiosResponse<ServerDto> = await edgeApi.get<ServerDto>(
        microServicesRoutes.edge.get.organizations.getForManagementNest,
        { params: { page: pageNumber, pageLimit, search: safeSearch } }
      );

      return {
        success: true,
        data: res.data?.data ?? [],
        totalPages: res.data?.totalPages,
        totalCount: res.data?.totalCount,
        pageNumber: res.data?.pageNumber,
      };
    } catch (e) {
      return handleError<OrganizationManagement[]>(
        'Error trying to get organizations',
        e,
        'Edge getOrganizations'
      );
    }
    }, [handleError]
  );

  const getActiveOrganizationsAsync = useCallback(
    async (): Promise<ApiResponse<ActiveOrganizationsResponse[]>> => {
      try {
        const url = microServicesRoutes.edge.get.organizations.getAllActiveOrganizations;

      type ServerDto =
        | ActiveOrganizationsResponse[]
        | { data?: ActiveOrganizationsResponse[]; activeOrganizations?: ActiveOrganizationsResponse[] };

      const response = await edgeApi.get<ServerDto>(url);
      const list: ActiveOrganizationsResponse[] = Array.isArray(response.data)
        ? response.data
        : response.data?.data ?? response.data?.activeOrganizations ?? [];

      return { success: true, data: list };
      } catch (e) {
        return handleError<ActiveOrganizationsResponse[]>(
          'Error fetching active organizations.',
          e,
          'getActiveOrganizationsAsync'
        );
      }
    }, [handleError]
  );

  const createOrganizationAsync = useCallback(
    async (
      organization: CreateOrganizationDto
    ): Promise<ApiResponse<OrganizationManagement>> => {
      try {
        const body: { name: string; adminsId: string[]; } = {
          name: organization.name,
          adminsId: organization.adminsId ?? [],
        };
        const res = await edgeApi.post<OrganizationManagement>(
          microServicesRoutes.edge.post.organizations.createOrganization,
          body
        );

        return { success: true, data: res.data };
      } catch (e) {
        return handleError<OrganizationManagement>(
          'Error trying to create organization',
          e,
          'Edge createOrganization'
        );
      }
    }, [handleError]
  );

  const updateOrganizationAsync = useCallback(
    async (
      organization: UpdateOrganizationDto
    ): Promise<ApiResponse<OrganizationManagement>> => {
      try {
        const compatId = organization._id ?? organization.id ?? '';
        const url = `${microServicesRoutes.edge.put.organizations.updateOrganization}/${compatId}`;

        const body: Record<string, unknown> = {
          ...organization,
          id: compatId,
          adminsId: organization.adminsId ?? [],
        };

        const res = await edgeApi.put<OrganizationManagement>(url, body);
        return { success: true, data: res.data };
      } catch (e) {
        return handleError<OrganizationManagement>(
          'Error trying to update organization',
          e,
          'Edge updateOrganization'
        );
      }
    }, [handleError]);

  const deleteOrganizationAsync = useCallback(
    async (id: string): Promise<ApiResponse<OrganizationManagement>> => {
      try {
        const res = await edgeApi.delete<OrganizationManagement>(
          `${microServicesRoutes.edge.delete.organizations.deleteOrganization}/${id}`
        );
        return { success: true, data: res.data };
      } catch (e) {
        return handleError<OrganizationManagement>(
          'Error trying to delete organization',
          e,
          'Edge deleteOrganization'
        );
      }
    }, [handleError]);

  return useMemo(() => ({
    getOrganizationsAsync,
    getActiveOrganizationsAsync,
    createOrganizationAsync,
    updateOrganizationAsync,
    deleteOrganizationAsync,
  }), [
    getOrganizationsAsync,
    getActiveOrganizationsAsync,
    createOrganizationAsync,
    updateOrganizationAsync,
    deleteOrganizationAsync,
  ]);
}
